import type { SortingState, Updater } from "@tanstack/react-table"
import { type RefObject, useCallback, useMemo } from "react"

import {
  type WarehouseReceivingTableSearchParams,
  type WarehouseReceivingTableStoreSelectors,
  useWarehouseReceivingTableBase,
} from "../../../hooks"
import { type StoredGoodsReceiptPalletsSearchFilters, useStoredGoodsReceiptPalletsSearchStore } from "../stores"

import type { GoodsReceiptPallet, GoodsReceiptPalletSortField, Lookup, PageInfo } from "@/graphql/codegen/graphql"
import { queryClient } from "@/providers/GraphqlRouterProvider"
import type { Option } from "@/types/form-fields"

interface UseStoredGoodsReceiptPalletsTableParams {
  routePath: "/app/warehouse-receiving" | "/app/tablet/warehouse-receiving"
}

interface UseStoredGoodsReceiptPalletsTableReturn {
  goodsReceiptPallets: GoodsReceiptPallet[]
  isGoodsReceiptPalletsFetching: boolean
  searchQuery: string
  warehouseAreaIds: string[]
  warehouses: Lookup[]
  warehouseOptions: Option<string>[]
  isWarehousesFetching: boolean
  sortingState: SortingState
  tableContainerRef: RefObject<HTMLDivElement>
  pageInfo: PageInfo | undefined
  handleRefetch: () => void
  handleSearchChange: (query: string) => void
  handleFilterChange: (warehouseIds: string[]) => void
  handleFilterRemove: (warehouseId: string) => void
  handleFilterClear: () => void
  handleSortingChange: (updaterOrValue: Updater<SortingState>) => void
  handleScroll: () => void
}

export const useStoredGoodsReceiptPalletsTable = ({
  routePath,
}: UseStoredGoodsReceiptPalletsTableParams): UseStoredGoodsReceiptPalletsTableReturn => {
  const data = useStoredGoodsReceiptPalletsSearchStore((state) => state.data)
  const fetching = useStoredGoodsReceiptPalletsSearchStore((state) => state.fetching)
  const executeSearch = useStoredGoodsReceiptPalletsSearchStore((state) => state.executeSearch)
  const loadMore = useStoredGoodsReceiptPalletsSearchStore((state) => state.loadMore)
  const clearStore = useStoredGoodsReceiptPalletsSearchStore((state) => state.clearStore)
  const pageInfo = useStoredGoodsReceiptPalletsSearchStore((state) => state.pageInfo)

  const reactiveStoreSelectors: WarehouseReceivingTableStoreSelectors<
    GoodsReceiptPallet,
    StoredGoodsReceiptPalletsSearchFilters
  > = useMemo(
    () => ({
      data,
      fetching,
      executeSearch: executeSearch as any,
      loadMore: loadMore as any,
      clearStore,
      pageInfo,
    }),
    [data, fetching, executeSearch, loadMore, clearStore, pageInfo]
  )

  const buildSearchParams = useCallback(
    (baseParams: WarehouseReceivingTableSearchParams): StoredGoodsReceiptPalletsSearchFilters =>
      baseParams as StoredGoodsReceiptPalletsSearchFilters,
    []
  )

  const baseHookReturn = useWarehouseReceivingTableBase<
    GoodsReceiptPallet,
    GoodsReceiptPalletSortField,
    StoredGoodsReceiptPalletsSearchFilters
  >({
    store: reactiveStoreSelectors,
    searchParamsRoute: `${routePath}/_layout/stored`,
    navigationRoute: `${routePath}/stored`,
    buildSearchParams,
    isExpandable: false,
    queryClient,
  })

  return {
    goodsReceiptPallets: baseHookReturn.data,
    isGoodsReceiptPalletsFetching: baseHookReturn.isFetching,
    searchQuery: baseHookReturn.searchQuery,
    warehouseAreaIds: baseHookReturn.warehouseAreaIds,
    warehouses: baseHookReturn.warehouses,
    warehouseOptions: baseHookReturn.warehouseOptions,
    isWarehousesFetching: baseHookReturn.isWarehousesFetching,
    sortingState: baseHookReturn.sortingState,
    tableContainerRef: baseHookReturn.tableContainerRef,
    pageInfo: baseHookReturn.pageInfo,
    handleRefetch: baseHookReturn.handleRefetch,
    handleSearchChange: baseHookReturn.handleSearchChange,
    handleFilterChange: baseHookReturn.handleFilterChange,
    handleFilterRemove: baseHookReturn.handleFilterRemove,
    handleFilterClear: baseHookReturn.handleFilterClear,
    handleSortingChange: baseHookReturn.handleSortingChange,
    handleScroll: baseHookReturn.handleScroll,
  }
}
